import React from 'react';

import { useNavigate } from 'react-router-dom';

import { Box, Icon, Stack, Typography } from '@liscio/ui';

import {
  useAccountStepSkipped,
  useCreatedAccountName,
  useSelectedAccountName,
  useSelectedContactDisplayName,
  useSelectedContactPrimaryEmail,
} from '../../hooks/useOnboardingStore';
import {
  CREATE_ACCOUNT_AND_CONTACT_SUB_PATHS,
  getCreateAccountAndContactSubRoute,
} from '../CreateAccountAndContactView/route-constants';

export const FileAssociationPreview: React.FC = () => {
  const navigate = useNavigate();
  const contactDisplayName = useSelectedContactDisplayName();
  const contactEmail = useSelectedContactPrimaryEmail();
  const createdAccountName = useCreatedAccountName();
  const selectedAccountName = useSelectedAccountName();
  const accountStepSkipped = useAccountStepSkipped();

  const hasContact = Boolean(contactDisplayName || contactEmail);

  if (!hasContact) {
    return null;
  }

  const contactName =
    contactDisplayName?.trim() || contactEmail?.trim() || 'Contact';
  const accountName = accountStepSkipped
    ? undefined
    : createdAccountName?.trim() || selectedAccountName?.trim();

  const handleChange = () => {
    navigate(
      `/onboarding${getCreateAccountAndContactSubRoute(CREATE_ACCOUNT_AND_CONTACT_SUB_PATHS.createAccount)}`
    );
  };

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.300',
        bgcolor: 'grey.50',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          File will be associated with
        </Typography>
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={handleChange}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            fontWeight: 600,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0,
          }}
        >
          <Icon icon="pencil" sx={{ fontSize: 14 }} />
          Change
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems="stretch"
        spacing={1.5}
      >
        <Box
          sx={{
            flex: 1,
            width: '100%',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" width="100%">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon icon="user" sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Contact
              </Typography>
              <Typography variant="body2" fontWeight={700} noWrap>
                {contactName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                display="block"
                sx={{ minHeight: '1.25rem' }}
              >
                {contactEmail || '\u00A0'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            alignSelf: 'center',
          }}
        >
          <Icon icon="arrowRight" sx={{ fontSize: 12 }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            width: '100%',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" width="100%">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon icon="bank" sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Account
              </Typography>
              <Typography variant="body2" fontWeight={700} noWrap>
                {accountName ?? 'No account, contact only'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                aria-hidden
                sx={{ visibility: 'hidden' }}
              >
                {'\u00A0'}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default FileAssociationPreview;
